import LongPressClipper from "@/components/LongPressClipper";
import { ArrowRight, Clock, Sparkles, FileQuestion, Lock, Blend, Gamepad2, CheckCircle2 } from "lucide-react";

/**
 * BentoStepCard — Iter84 extraction.
 *
 * One bento card for a single learning-path step. Receives all
 * orchestrating callbacks from BentoPath (no API calls here; this is
 * a pure presentational component bar the `scrollIntoView` ref the
 * Next-Step button uses).
 */
export default function BentoStepCard({
  step, index, accent, span, isCompleted, isLastStep, generating,
  pathId, onExplain, onMode, onExam, onNextStep,
}) {
  const i = index;
  const s = step;
  return (
    <article
      className={`glass p-6 md:p-7 ${span} border ${accent.border} flex flex-col gap-4 relative overflow-hidden transition-all hover:-translate-y-1 animate-fadeup`}
      style={{ animationDelay: `${i * 60}ms` }}
      data-testid={`path-step-${i}`}
    >
      <div className={`absolute -top-10 -right-10 w-40 h-40 rounded-full ${accent.dot} opacity-[0.07] blur-2xl`} />
      {isCompleted && (
        <span
          className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full border border-[#00FF9D]/40 bg-[#00FF9D]/[0.12] px-2.5 py-0.5 text-[10px] font-display font-semibold text-[#00FF9D] uppercase tracking-wider"
          data-testid={`step-completed-badge-${i}`}
        >
          <CheckCircle2 size={11} /> Completed
        </span>
      )}
      <div className="flex items-center gap-2">
        <span className={`label-eyebrow ${accent.badge} px-2 py-0.5 rounded-full !text-[10px]`}>Step {i + 1}</span>
        <span className="flex items-center gap-1 text-[11px] text-white/50" data-testid={`step-duration-${i}`}>
          <Clock size={11} /> 30 Minutes
        </span>
      </div>
      <h3 className="font-display text-xl md:text-2xl tracking-tight leading-snug">{s.title}</h3>
      <LongPressClipper
        pathId={pathId}
        stepIndex={i}
        title={s.title}
        className="space-y-2 select-text"
      >
        {s.objective && <div className="text-white/70 text-sm">{s.objective}</div>}
        {s.detail && <div className="text-white/55 text-sm leading-relaxed">{s.detail}</div>}
      </LongPressClipper>
      {Array.isArray(s.key_terms) && s.key_terms.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-auto">
          {s.key_terms.slice(0, 5).map((k) => (
            <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-white/60 border border-white/10">
              {k}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5 mt-1">
        <button
          onClick={() => onExplain(i)}
          className="text-xs px-3 py-2 rounded-lg btn-mint-outline flex items-center gap-1.5 relative"
          data-testid={`btn-explain-deeper-${i}`}
        >
          <Sparkles size={12} /> Explain deeper
          <span
            className="ml-1 text-[9px] font-display font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-[#FF7B00] to-[#5D3FD3] text-white"
            data-testid={`pro-badge-explain-${i}`}
          >
            PRO
          </span>
        </button>
        <button
          onClick={() => onMode(i, "simulate")}
          className="text-xs px-3 py-2 rounded-lg border border-[#0044FF]/35 text-[#8aa8ff] bg-[#0044FF]/5 hover:bg-[#0044FF]/15 flex items-center gap-1.5 transition"
          data-testid={`btn-simulate-${i}`}
        >
          <Gamepad2 size={12} /> Simulate
        </button>
        <button
          onClick={() => onMode(i, "analogy")}
          className="text-xs px-3 py-2 rounded-lg border border-[#ffb067]/35 text-[#ffb067] bg-[#ffb067]/5 hover:bg-[#ffb067]/15 flex items-center gap-1.5 transition"
          data-testid={`btn-analogy-${i}`}
        >
          <Blend size={12} /> Lens
        </button>
        <button
          onClick={() => onExam(i)}
          disabled={generating !== null}
          className="text-xs px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 flex items-center gap-1.5 disabled:opacity-50"
          data-testid={`btn-exam-step-${i}`}
        >
          {generating === i ? (
            <>Generating…</>
          ) : (
            <><FileQuestion size={12} /> Exam · 1 <Lock size={10} className="opacity-60" /></>
          )}
          <ArrowRight size={12} />
        </button>
        {!isLastStep && (
          <button
            onClick={() => {
              const next = document.querySelector(`[data-testid="path-step-${i + 1}"]`);
              if (next) {
                next.scrollIntoView({ behavior: "smooth", block: "start" });
                onNextStep?.(i + 1);
              }
            }}
            className="text-xs px-3 py-2 rounded-lg bg-gradient-to-r from-[#0044FF] to-[#5D3FD3] text-white border border-white/10 hover:opacity-90 flex items-center gap-1.5 font-display font-semibold ml-auto"
            data-testid={`btn-next-step-${i}`}
          >
            Next Step <ArrowRight size={12} />
          </button>
        )}
      </div>
    </article>
  );
}
